import { useEffect, useState } from 'react'
import {
  ExplicitSelectionButton,
  feedbackcard,
  SkeletonDots
} from '@yes.technology/react-toolkit'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18n-lite'

import useSitemodelFromUrl from 'site/shared/hooks/useSitemodelFromUrl'
import TopLine from 'site/Renderer/components/TopLine'
import { TopLineProps } from 'site/Renderer/components/TopLine/TopLine.types'
import { AlignEnum, RepresentationEnum } from 'site/shared/enums'
import apiAsync from 'infrastructure/shared/api/api-async'
import { Container, StyledSitemodelView } from './EditTools.styles'
import SideMenu from './SideMenu'
import JsonEditor from './JsonEditor'
import SectionDivider from './SectionDivider'
import SitemodelView from './SitemodelView'

const EditTools = () => {
  const { t } = useTranslation()

  const { sitemodel, refetchSitemodelFromUrl, isLoading, isFetched } =
    useSitemodelFromUrl({
      enabled: true
    })

  const sitemodelContent = sitemodel?.sitemodel_content

  const [viewType, setViewType] = useState<string>('view')

  const [objectState, setObjectState] = useState<object>({})

  useEffect(() => {
    if (sitemodelContent && typeof sitemodelContent === 'string') {
      setObjectState(JSON.parse(sitemodelContent))
    }
  }, [sitemodelContent])

  const propsTopLine: TopLineProps['props'] = {
    layout: {
      position_1: [
        {
          order: 1,
          align: AlignEnum.Right,
          representation: RepresentationEnum.LogoArea,
          image: {
            desktop: {
              alt: 'Logotipo para desktop',
              src: 'https://d2qqjx0lx9cu56.cloudfront.net/logos/logo-yes.svg'
            }
          },
          des: 'Yes Network',
          link: '/'
        }
      ],
      position_3: [
        {
          order: 1,
          align: AlignEnum.Left,
          representation: RepresentationEnum.ManagementDashboard,
          components: [
            {
              uuid: 'f91fae3d-c3b4-4d09-b6b9-476ebf6a4b10',
              des: 'Login'
            }
          ]
        }
      ]
    }
  }

  const handleSave = async () => {
    const response = await apiAsync.requestSync({
      endpoint: 'msPlatform',
      path: `objects/${sitemodel?.uuid}`,
      method: 'PATCH',
      obj: {
        uuid_Objectclass: globalThis.idObjectclassSitemodel,
        sitemodel_content: JSON.stringify(objectState),
        version: sitemodel?.version
      },
      showLoading: true
    })

    if (response.success) {
      feedbackcard('Sucesso!', {
        message: t('edit-tools.save-success'),
        type: 'success'
      })
      refetchSitemodelFromUrl()
    } else {
      feedbackcard('Erro!', {
        message: response.message || t('edit-tools.save-error'),
        type: 'error'
      })
    }
  }

  return (
    <div>
      <Helmet>
        <title>{t('edit-tools.title')}</title>
      </Helmet>

      <TopLine props={propsTopLine} />

      <SectionDivider onSave={handleSave} />

      {isFetched && !sitemodel && !isLoading && (
        <div>{t('edit-tools.sitemodel-loading-error')}</div>
      )}

      {!sitemodelContent && (
        <div>{t('edit-tools.sitemodel-content-not-found')}</div>
      )}

      {isLoading && <SkeletonDots />}

      {isFetched && sitemodel && (
        <Container>
          <SideMenu sitemodel={sitemodel} />
          <StyledSitemodelView>
            <ExplicitSelectionButton
              direction='horizontal'
              heightClass='small'
              onChange={(option) => setViewType(option.uuid)}
              checkedOption={viewType}
              optionLabelsPosition='left'
              options={[
                {
                  uuid: 'view',
                  des: 'View'
                },
                {
                  uuid: 'json-editor',
                  des: 'JSON Editor'
                }
              ]}
            />

            {viewType === 'view' && <SitemodelView {...objectState} />}

            {viewType === 'json-editor' && (
              <JsonEditor initialJson={objectState} onChange={setObjectState} />
            )}
          </StyledSitemodelView>
        </Container>
      )}
    </div>
  )
}

export default EditTools
